import { useCallback, useEffect, useMemo, useState } from 'react';
import { Box, Checkbox, Divider, FormControlLabel, IconButton, Typography } from '@mui/material';
import { ArrowLeftIcon, PlusIcon, ArrowRightIcon, SearchIcon } from '~/assets/icons';
import dayjs from 'dayjs';
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore';
import advancedFormat from 'dayjs/plugin/advancedFormat';
import { BaseTab, BaseTabList } from '~/components/common';
import { useTranslation } from 'react-i18next';
import Calendar from '~/components/schedules/calendar-v2/Calendar';
import TeamCalendarGrid from '~/components/schedules/team-calendar-grid/TeamCalendarGrid';
import { Views, ViewsProps } from 'react-big-calendar';
import { CalendarViewMode } from '~/utils/types/schedule';
import { useAppDispatch, useAppSelector } from '~/redux/hooks';
import { scheduleActions } from '~/thunks/schedule/scheduleSlice';
import { handleGetEvents } from '~/thunks/schedule/scheduleThunk';
import EditEventDialog from '~/components/schedules/edit-event-dialog/EditEventDialog';
import AddAppointmentModal from '~/components/schedules/add-appointment-modal/AddAppointmentModal';
import { DIALOG_MODE_CREATE } from '~/utils/constants/common';
import { IEditEventPayload, IScheduleEvent } from '~/utils/interface/schedule';
import { catchError, notify } from '~/utils/helper';
import ModalAlertDelete from '~/components/common/modal-alert-delete/ModalAlertDelete';
import scheduleEventApi from '~/apis/schedule';
import styles from './Schedule.module.scss';
import classNames from 'classnames/bind';
import { CheckIcon } from 'lucide-react';
import useDebounce from '~/hooks/useDebounce';
import userManagementApi, { IEmployee, IParamsGetEmployees } from '~/apis/userManagement';
import PrimaryButton from '~/components/common/primary-button/PrimaryButton';

const cx = classNames.bind(styles);

// Helper function to get initials from name
const getInitials = (firstName?: string, lastName?: string, username?: string): string => {
  const firstInitial = firstName?.charAt(0) || '';
  const lastInitial = lastName?.charAt(0) || '';

  if (firstInitial || lastInitial) {
    return `${firstInitial}${lastInitial}`.toUpperCase();
  }

  // Fallback to username first character
  return username?.charAt(0)?.toUpperCase() || 'N/A';
};

// CheckIcon SVG component
const CheckIconSVG = () => (
  <svg width="14" height="11" viewBox="0 0 14 11" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M13 1L4.75 10L1 5.90909" stroke="#1CA78B" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

dayjs.extend(isSameOrBefore);
dayjs.extend(advancedFormat);

const views: ViewsProps = {
  month: true,
  week: true,
  day: true,
};

const APPOINTMENT_TYPES = [
  'Task',
  'Phone Call',
  'Initial Appointment',
  'Adjuster Meeting',
  'Estimate Follow Up',
  'Invoice Follow Up',
  'Meeting',
  'Collections Follow Up',
  'Job Notes',
];

const Schedule = () => {
  const [openEditDialog, setOpenEditDialog] = useState(false);
  const [editEventDialogMode, setEditEventDialogMode] = useState<'create' | 'edit'>(DIALOG_MODE_CREATE);
  const [editData, setEditData] = useState<IEditEventPayload | undefined>(undefined);
  const [openAddAppointmentModal, setOpenAddAppointmentModal] = useState(false);
  const [appointmentModalMode, setAppointmentModalMode] = useState<'add' | 'view' | 'edit'>('add');
  const [selectedScheduleId, setSelectedScheduleId] = useState<number | undefined>(undefined);
  const [teamMembersSearch, setTeamMembersSearch] = useState<string>('');
  const [employees, setEmployees] = useState<IEmployee[]>([]);
  const [selectedTeamMembers, setSelectedTeamMembers] = useState<Set<number>>(new Set());
  const [isLoadingEmployees, setIsLoadingEmployees] = useState(false);
  const [subcontractorsSearch, setSubcontractorsSearch] = useState<string>('');
  const [subcontractors, setSubcontractors] = useState<IEmployee[]>([]);
  const [selectedSubcontractors, setSelectedSubcontractors] = useState<Set<number>>(new Set());
  const [isLoadingSubcontractors, setIsLoadingSubcontractors] = useState(false);
  const [selectedAppointmentTypes, setSelectedAppointmentTypes] = useState<Set<string>>(new Set());
  const [calendarLayoutMode, setCalendarLayoutMode] = useState<'time' | 'resource'>('time');

  const debouncedTeamMembersSearch = useDebounce(teamMembersSearch, 500);
  const debouncedSubcontractorsSearch = useDebounce(subcontractorsSearch, 500);

  const { currentTime, calendarViewMode, selectedEventId, openAlertDelete, needRefreshData, events: reduxEvents } =
    useAppSelector((state) => state.schedule);
  const dispatch = useAppDispatch();

  const { t } = useTranslation(['admin/schedule', 'admin/common']);

  const goPreviousTime = () => {
    dispatch(scheduleActions.goPreviousTime());
  };
  const goNextTime = () => {
    dispatch(scheduleActions.goNextTime());
  };

  const handleChangeCalendarView = (_event: React.ChangeEvent<{}>, newValue: CalendarViewMode): void => {
    dispatch(scheduleActions.changeCalendarViewMode(newValue));
  };

  const handleSetOpen = useCallback(
    (open: boolean, needRefreshData?: boolean) => {
      setOpenEditDialog(open);
      if (needRefreshData) {
        dispatch(scheduleActions.changeNeedRefreshData(needRefreshData));
      }
    },
    [dispatch]
  );

  useEffect(() => {
    const from = dayjs(currentTime).startOf(calendarViewMode).format('YYYY-MM-DD');
    const to = dayjs(currentTime).endOf(calendarViewMode).format('YYYY-MM-DD');

    const assigneeIds = Array.from(selectedTeamMembers);
    const subcontractorIds = Array.from(selectedSubcontractors);
    const appointmentTypes = Array.from(selectedAppointmentTypes);

    dispatch(
      handleGetEvents({
        from,
        to,
        assigneeId: assigneeIds.length > 0 ? assigneeIds : undefined,
        subcontractorId: subcontractorIds.length > 0 ? subcontractorIds : undefined,
        appointmentType: appointmentTypes.length > 0 ? appointmentTypes : undefined,
      })
    );
  }, [dispatch, currentTime, calendarViewMode, selectedTeamMembers, selectedSubcontractors, selectedAppointmentTypes]);

  useEffect(() => {
    const from = dayjs(currentTime).startOf(calendarViewMode).format('YYYY-MM-DD');
    const to = dayjs(currentTime).endOf(calendarViewMode).format('YYYY-MM-DD');

    if (needRefreshData) {
      const assigneeIds = Array.from(selectedTeamMembers);
      const subcontractorIds = Array.from(selectedSubcontractors);
      const appointmentTypes = Array.from(selectedAppointmentTypes);

      dispatch(
        handleGetEvents({
          from,
          to,
          assigneeId: assigneeIds.length > 0 ? assigneeIds : undefined,
          subcontractorId: subcontractorIds.length > 0 ? subcontractorIds : undefined,
          appointmentType: appointmentTypes.length > 0 ? appointmentTypes : undefined,
        })
      );
    }
  }, [dispatch, currentTime, calendarViewMode, needRefreshData, selectedTeamMembers, selectedSubcontractors, selectedAppointmentTypes]);

  // Auto-switch to Team View when Week mode is selected
  useEffect(() => {
    if (calendarViewMode === 'week') {
      setCalendarLayoutMode('resource');
    } else {
      setCalendarLayoutMode('time');
    }
  }, [calendarViewMode]);

  useEffect(() => {
    const fetchEmployees = async () => {
      try {
        setIsLoadingEmployees(true);
        const params: IParamsGetEmployees = {
          page: 1,
          limit: 50,
          status: 'ACTIVE',
        };

        if (debouncedTeamMembersSearch) {
          params.textSearch = debouncedTeamMembersSearch;
        }

        const response = await userManagementApi.getEmployees(params);
        if (response.data?.data?.responses) {
          setEmployees(response.data.data.responses);
        }
      } catch (error) {
        catchError(error, (message) => {
          console.error('Error fetching employees:', message);
        });
      } finally {
        setIsLoadingEmployees(false);
      }
    };

    fetchEmployees();
  }, [debouncedTeamMembersSearch]);

  useEffect(() => {
    const fetchSubcontractors = async () => {
      try {
        setIsLoadingSubcontractors(true);
        const params: IParamsGetEmployees = {
          page: 1,
          limit: 50,
          status: 'ACTIVE',
          role: '00004', // Subcontractor role
        };

        if (debouncedSubcontractorsSearch) {
          params.textSearch = debouncedSubcontractorsSearch;
        }

        const response = await userManagementApi.getEmployees(params);
        if (response.data?.data?.responses) {
          setSubcontractors(response.data.data.responses);
        }
      } catch (error) {
        catchError(error, (message) => {
          console.error('Error fetching subcontractors:', message);
        });
      } finally {
        setIsLoadingSubcontractors(false);
      }
    };

    fetchSubcontractors();
  }, [debouncedSubcontractorsSearch]);

  const handleTeamMemberToggle = useCallback((employeeId: number) => {
    setSelectedTeamMembers((prev) => {
      const isShowAll = prev.size === 0;
      const newSet = isShowAll
        ? new Set(employees.map((e) => e.id).filter((id) => id !== employeeId))
        : new Set(prev);
      if (!isShowAll) {
        if (newSet.has(employeeId)) {
          newSet.delete(employeeId);
        } else {
          newSet.add(employeeId);
        }
      }
      return newSet.size === employees.length ? new Set<number>() : newSet;
    });
  }, [employees]);

  const handleSubcontractorToggle = useCallback((employeeId: number) => {
    setSelectedSubcontractors((prev) => {
      const isShowAll = prev.size === 0;
      const newSet = isShowAll
        ? new Set(subcontractors.map((s) => s.id).filter((id) => id !== employeeId))
        : new Set(prev);
      if (!isShowAll) {
        if (newSet.has(employeeId)) {
          newSet.delete(employeeId);
        } else {
          newSet.add(employeeId);
        }
      }
      return newSet.size === subcontractors.length ? new Set<number>() : newSet;
    });
  }, [subcontractors]);

  const handleAppointmentTypeToggle = useCallback((appointmentType: string) => {
    setSelectedAppointmentTypes((prev) => {
      const isShowAll = prev.size === 0;
      const newSet = isShowAll
        ? new Set(APPOINTMENT_TYPES.filter((t) => t !== appointmentType))
        : new Set(prev);
      if (!isShowAll) {
        if (newSet.has(appointmentType)) {
          newSet.delete(appointmentType);
        } else {
          newSet.add(appointmentType);
        }
      }
      return newSet.size === APPOINTMENT_TYPES.length ? new Set<string>() : newSet;
    });
  }, []);

  const timeValue = useMemo(() => {
    switch (calendarViewMode) {
      case Views.WEEK:
        const startOfWeek = dayjs(currentTime).startOf('week').add(1, 'day');
        const endOfWeek = dayjs(currentTime).endOf('week').add(1, 'day');
        const startMonth = startOfWeek.format('MMMM');
        const endMonth = endOfWeek.format('MMMM');
        const startDay = startOfWeek.format('D');
        const endDay = endOfWeek.format('D');
        const year = startOfWeek.format('YYYY');

        if (startMonth === endMonth) {
          return `${startMonth} ${startDay} to ${endDay} ${year}`;
        } else {
          return `${startMonth} ${startDay} to ${endMonth} ${endDay} ${year}`;
        }
      case Views.DAY:
        return dayjs(currentTime).format('MMMM Do YYYY');
      default:
        return dayjs(currentTime).format('MMMM YYYY');
    }
  }, [calendarViewMode, currentTime]);

  const closeAlertDeleteModal = useCallback(() => {
    dispatch(scheduleActions.changeOpenAlertDelete(false));
  }, [dispatch]);

  const handleDeleteSchedule = useCallback((scheduleId: number) => {
    setOpenAddAppointmentModal(false);
    dispatch(scheduleActions.changeNeedRefreshData(true));
  }, [dispatch]);

  const handleEditSchedule = useCallback((scheduleId: number) => {
    setAppointmentModalMode('edit');
    setSelectedScheduleId(scheduleId);
  }, []);

  const onDeleteSelectedEvent = useCallback(async () => {
    try {
      if (selectedEventId) {
        await scheduleEventApi.deleteEvent({
          ids: [selectedEventId],
        });
      }
      notify('success', t('deleteSuccessMessage'));
      dispatch(scheduleActions.changeNeedRefreshData(true));
      dispatch(scheduleActions.changeOpenAlertDelete(false));
    } catch (e: unknown) {
      catchError(e, (message) => {
        notify('error', message);
      });
    }
  }, [dispatch, t, selectedEventId]);

  const onSelectEvent = useCallback((event: IScheduleEvent) => {
    setAppointmentModalMode('edit');
    setSelectedScheduleId(parseInt(String(event.originalScheduleId || event.id), 10));
    setOpenAddAppointmentModal(true);
  }, []);

  // Helper function to get assignee info from employees list
  const getAssigneeInfo = useCallback((assigneeId: string, assigneeName: string | null) => {
    if (assigneeName) {
      return { id: assigneeId, name: assigneeName, avatar: '' };
    }

    // If assigneeName is null, try to find it in employees list
    const employee = employees.find(emp => emp.id === Number(assigneeId));
    if (employee) {
      return {
        id: assigneeId,
        name: `${employee.profile.firstName} ${employee.profile.lastName}`.trim() || employee.username,
        avatar: employee.profile.avatar?.url || '',
      };
    }

    // Fallback: use ID as name
    return { id: assigneeId, name: `User ${assigneeId}`, avatar: '' };
  }, [employees]);

  // Process events from Redux and split multi-day schedules
  const events = useMemo(() => {
    if (reduxEvents.length === 0) return [];

    const splitSchedules: IScheduleEvent[] = [];

    reduxEvents.forEach((schedule) => {
      const startDate = dayjs(schedule.startAt);
      const endDate = dayjs(schedule.endAt);

      if (!startDate.isSame(endDate, 'day')) {
        let currentDate = startDate;
        let dayIndex = 0;

        while (currentDate.isSameOrBefore(endDate, 'day')) {
          const isFirstDay = dayIndex === 0;
          const isLastDay = currentDate.isSame(endDate, 'day');

          let dayStartTime, dayEndTime;

          if (schedule.allDayChecked) {
            dayStartTime = currentDate.startOf('day').toDate();
            dayEndTime = currentDate.endOf('day').toDate();
          } else {
            if (isFirstDay) {
              dayStartTime = startDate.toDate();
              dayEndTime = currentDate.endOf('day').toDate();
            } else if (isLastDay) {
              dayStartTime = currentDate.startOf('day').toDate();
              dayEndTime = endDate.toDate();
            } else {
              dayStartTime = currentDate.startOf('day').toDate();
              dayEndTime = currentDate.endOf('day').toDate();
            }
          }

          splitSchedules.push({
            id: `${schedule.scheduleId}_${currentDate.format('YYYY-MM-DD')}_${dayIndex}`,
            name: schedule.appointmentTitle,
            description: schedule.description || '',
            priority: schedule.priority?.toLowerCase() || 'medium',
            startAt: dayStartTime,
            endAt: dayEndTime,
            startTime: dayjs(dayStartTime).format('HH:mm:ss+07:00'),
            endTime: dayjs(dayEndTime).format('HH:mm:ss+07:00'),
            assigneeColor: schedule.assigneeColor || '#3b82f6',
            assignees: schedule.assigneeId ? [getAssigneeInfo(schedule.assigneeId, schedule.assignee)] : null,
            appointmentType: schedule.appointmentType,
            isRecurring: schedule.isRecurring,
            relatedJob: schedule.relatedJob || undefined,
            subcontractor: schedule.subcontractor || undefined,
            subcontractorColor: schedule.subcontractorColor || undefined,
            originalScheduleId: schedule.scheduleId,
            occurrenceId: schedule.occurrenceId,
          });

          currentDate = currentDate.add(1, 'day');
          dayIndex++;
        }
      } else {
        splitSchedules.push({
          id: schedule.scheduleId,
          name: schedule.appointmentTitle,
          description: schedule.description || '',
          priority: schedule.priority?.toLowerCase() || 'medium',
          startAt: new Date(schedule.startAt),
          endAt: new Date(schedule.endAt),
          startTime: dayjs(schedule.startAt).format('HH:mm:ss+07:00'),
          endTime: dayjs(schedule.endAt).format('HH:mm:ss+07:00'),
          assigneeColor: schedule.assigneeColor || '#3b82f6',
          assignees: schedule.assigneeId ? [getAssigneeInfo(schedule.assigneeId, schedule.assignee)] : null,
          appointmentType: schedule.appointmentType,
          isRecurring: schedule.isRecurring,
          relatedJob: schedule.relatedJob || undefined,
          subcontractor: schedule.subcontractor || undefined,
          subcontractorColor: schedule.subcontractorColor || undefined,
          originalScheduleId: schedule.scheduleId,
          occurrenceId: schedule.occurrenceId,
        });
      }
    });

    return splitSchedules;
  }, [reduxEvents, getAssigneeInfo]);

  // Prepare resources from employees for resource view
  const resources = useMemo(() => {
    if (calendarLayoutMode !== 'resource') return undefined;

    // Filter employees based on selectedTeamMembers
    const filteredEmployees = selectedTeamMembers.size === 0
      ? employees
      : employees.filter(emp => selectedTeamMembers.has(emp.id));

    return filteredEmployees.map(employee => ({
      id: employee.id,
      name: `${employee.profile.firstName} ${employee.profile.lastName}`.trim() || employee.username,
      color: employee.profile.color || '#3B82F6',
      avatar: employee.profile.avatar?.url || null,
    }));
  }, [calendarLayoutMode, employees, selectedTeamMembers]);

  // Map events to resources (add resourceId based on assignee)
  const eventsWithResources = useMemo(() => {
    if (calendarLayoutMode !== 'resource') return events;

    return events.map(event => ({
      ...event,
      resourceId: event.assignees && event.assignees.length > 0 ? Number(event.assignees[0].id) : undefined,
    }));
  }, [calendarLayoutMode, events]);

  return (
    <Box className={cx('container')}>
      <Box className={cx('header')}>
        <Box className={cx('header-left')}>
          <Box className={cx('header-left')}>
            <BaseTabList
              value={calendarViewMode}
              onChange={handleChangeCalendarView}
              centered
              TabIndicatorProps={{ style: { backgroundColor: 'transparent' } }}
            >
              {Object.keys(views).map((view: string, index: number) => {
                return <BaseTab value={view} key={index} label={t(`datetime.${view}`, { ns: 'admin/common' })} />;
              })}
            </BaseTabList>
          </Box>
        </Box>
        <Box className={cx('header-right')}>
          <IconButton
            color='primary'
            className={cx('header-right-button')}
            onClick={goPreviousTime}
          >
            <ArrowLeftIcon className={cx('header-right-button-icon')} />
          </IconButton>
          <Typography className={cx('header-right-time')}>
            {timeValue}
          </Typography>
          <IconButton
            color='primary'
            className={cx('header-right-button')}
            onClick={goNextTime}
          >
            <ArrowRightIcon className={cx('header-right-button-icon')} />
          </IconButton>
        </Box>
      </Box>

      <Box className={cx('schedule-container')}>
        <Box className={cx('sidebar-container')}>

          <PrimaryButton
            title="Add Appointment"
            onClick={() => {
              setAppointmentModalMode('add');
              setSelectedScheduleId(undefined);
              setOpenAddAppointmentModal(true);
            }}
            className={cx('add-appointment-button')}
            icon={<PlusIcon className={cx('add-appointment-button-icon')} />}
          />

          <Box className={cx('sidebar-item')}>
            <Typography className={cx('item-title')}>Team Members</Typography>
            <div className={cx("search")}>
              <SearchIcon className={cx("search-icon")} />
              <input
                type="text"
                placeholder="Search"
                className={cx("search-input")}
                onChange={(e) => setTeamMembersSearch(e.target.value)}
                value={teamMembersSearch}
              />
            </div>

            <Divider className={cx('divider')} />

            <Box className={cx('item-content')}>
              {isLoadingEmployees ? (
                <Typography className={cx('item-content-loading')}>Loading...</Typography>
              ) : employees.length === 0 ? (
                <Typography className={cx('item-content-empty')}>No employees found</Typography>
              ) : (
                employees.map((employee) => (
                  <FormControlLabel
                    key={employee.id}
                    control={
                      <Checkbox
                        checked={selectedTeamMembers.size === 0 || selectedTeamMembers.has(employee.id)}
                        onChange={() => handleTeamMemberToggle(employee.id)}
                        icon={
                          <Box
                            className={cx('item-content-checkbox')}
                          />
                        }
                        checkedIcon={
                          <Box
                            className={cx('item-content-checkbox-checked')}
                          >
                            <CheckIcon className={cx('item-content-checkbox-checked-icon')} />
                          </Box>
                        }
                      />
                    }
                    label={<Typography className={cx('item-content-label')}>{`${employee.profile?.firstName || ''} ${employee.profile?.lastName || ''}`.trim() || employee.username}</Typography>}
                  />
                ))
              )}
            </Box>
          </Box>

          <Box className={cx('sidebar-item', 'mt-[40px]')}>
            <Typography className={cx('item-title')}>Subcontractors</Typography>

            <div className={cx("search")}>
              <SearchIcon className={cx("search-icon")} />
              <input
                type="text"
                placeholder="Search"
                className={cx("search-input")}
                onChange={(e) => setSubcontractorsSearch(e.target.value)}
                value={subcontractorsSearch}
              />
            </div>
            <Divider className={cx('divider')} />

            <Box className={cx('item-content')}>
              {isLoadingSubcontractors ? (
                <Typography className={cx('item-content-loading')}>Loading...</Typography>
              ) : subcontractors.length === 0 ? (
                <Typography className={cx('item-content-empty')}>No subcontractors found</Typography>
              ) : (
                subcontractors.map((subcontractor) => (
                  <FormControlLabel
                    key={subcontractor.id}
                    control={
                      <Checkbox
                        checked={selectedSubcontractors.size === 0 || selectedSubcontractors.has(subcontractor.id)}
                        onChange={() => handleSubcontractorToggle(subcontractor.id)}
                        icon={
                          <Box
                            className={cx('item-content-checkbox')}
                          />
                        }
                        checkedIcon={
                          <Box
                            className={cx('item-content-checkbox-checked')}
                          >
                            <CheckIcon className={cx('item-content-checkbox-checked-icon')} />
                          </Box>
                        }
                      />
                    }
                    label={<Typography className={cx('item-content-label')}>{`${subcontractor.profile?.firstName || ''} ${subcontractor.profile?.lastName || ''}`.trim() || subcontractor.username}</Typography>}
                  />
                ))
              )}
            </Box>
          </Box>

          <Box className={cx('sidebar-item', 'mt-[40px]')}>
            <Typography className={cx('item-title')}>Calendars</Typography>

            <div className={cx("search")}>
              <SearchIcon className={cx("search-icon")} />
              <input
                type="text"
                placeholder="Search"
                className={cx("search-input")}
                onChange={(e) => { }}
                value={''}
              />
            </div>
            <Divider className={cx('divider')} />
            <Box className={cx('item-content')}>
              {APPOINTMENT_TYPES.map((appointmentType) => (
                <FormControlLabel
                  key={appointmentType}
                  control={
                    <Checkbox
                      checked={selectedAppointmentTypes.size === 0 || selectedAppointmentTypes.has(appointmentType)}
                      onChange={() => handleAppointmentTypeToggle(appointmentType)}
                      icon={
                        <Box
                          className={cx('item-content-checkbox')}
                        />
                      }
                      checkedIcon={
                        <Box
                          className={cx('item-content-checkbox-checked')}
                        >
                          <CheckIcon className={cx('item-content-checkbox-checked-icon')} />
                        </Box>
                      }
                    />
                  }
                  label={<Typography className={cx('item-content-label')}>{appointmentType}</Typography>}
                />
              ))}
            </Box>
          </Box>

        </Box>
        <Box className={cx('calendar-container')}>
          {calendarLayoutMode === 'resource' && calendarViewMode === 'week' && resources ? (
            <TeamCalendarGrid
              teamMembers={resources}
              events={events}
              startDate={dayjs(currentTime).startOf('week').format('YYYY-MM-DD')}
              onEventClick={onSelectEvent}
            />
          ) : (
            (() => {
              const calendarProps: any = {
                view: calendarViewMode,
                date: currentTime,
                events,
                onSelectEvent,
              };

              return <Calendar {...calendarProps} />;
            })()
          )}

        </Box>
      </Box>

      <EditEventDialog open={openEditDialog} setOpen={handleSetOpen} mode={editEventDialogMode} data={editData} />

      <AddAppointmentModal
        open={openAddAppointmentModal}
        mode={appointmentModalMode}
        scheduleId={selectedScheduleId}
        onDelete={handleDeleteSchedule}
        onEdit={handleEditSchedule}
        onClose={() => {
          setOpenAddAppointmentModal(false);
          setAppointmentModalMode('add');
          setSelectedScheduleId(undefined);
          dispatch(scheduleActions.changeNeedRefreshData(true));
        }}
      />

      <ModalAlertDelete
        open={openAlertDelete}
        description={t('alertDescription')}
        onCancel={closeAlertDeleteModal}
        onConfirm={onDeleteSelectedEvent}
      />
    </Box>
  );
};

export default Schedule;
